"use client";

import React from "react";
import { Calendar, Clock, User, ArrowRight, Share2, Facebook, Twitter, Linkedin, Tag } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Card, CardContent, CardHeader, CardTitle, CardFooter, CardDescription } from "@/components/ui/card";
import { cn } from "@/lib/utils";


const getWvcClient = () => {
  if (typeof window !== "undefined" && (window as any).wvcClient) {
    return (window as any).wvcClient;
  }
  return {
    getCurrentWPQueryParams: () => ({ post_type: "post", p: 1 }), 
  };
};

function SinglePostConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="w-full max-w-4xl mx-auto space-y-8 animate-pulse">
        <div className="h-12 bg-muted rounded-md w-3/4" />
        <div className="h-6 bg-muted rounded-md w-1/2" />
        <div className="aspect-video bg-muted rounded-xl w-full" />
        <div className="space-y-4">
          <div className="h-4 bg-muted rounded w-full" />
          <div className="h-4 bg-muted rounded w-full" />
          <div className="h-4 bg-muted rounded w-5/6" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-20">
        <p className="text-destructive mb-4">Error loading post: {error}</p>
        <Button onClick={() => refetch()}>Try Again</Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="text-center py-20">
        <h2 className="text-2xl font-bold text-muted-foreground">Post not found</h2>
        <p className="mt-2 text-muted-foreground">The post you are looking for does not exist.</p>
        <div className="mt-6">
            <Link to="/">
                <Button variant="outline">Return Home</Button>
            </Link>
        </div>
      </div>
    );
  }

  const post = posts[0];
  const date = post.date ? new Date(post.date).toLocaleDateString("en-US", { year: 'numeric', month: 'long', day: 'numeric' }) : "";

  return (
    <article className="max-w-4xl mx-auto">
      {}
      <header className="mb-10 text-center md:text-left">
        <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-sm text-muted-foreground mb-6">
          {post.categories && post.categories.length > 0 && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary font-medium">
              <Tag className="size-3.5" />
              {post.categories[0].name}
            </span>
          )}
          <span className="flex items-center gap-1.5">
            <Calendar className="size-3.5" />
            {date}
          </span>
          <span className="flex items-center gap-1.5">
            <Clock className="size-3.5" />
            5 min read
          </span>
        </div>

        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground mb-8 leading-tight">
          {post.title}
        </h1>

        <div className="flex flex-col md:flex-row items-center justify-between gap-6 border-y border-border py-6">
          <div className="flex items-center gap-4">
            <Avatar className="size-12 border-2 border-background shadow-sm">
              <AvatarImage src={post.author?.avatar?.["96"] || ""} alt={post.author?.name || "Author"} />
              <AvatarFallback><User className="size-5" /></AvatarFallback>
            </Avatar>
            <div className="text-left">
              <p className="text-sm font-semibold text-foreground">{post.author?.name || "Unknown Author"}</p>
              <p className="text-xs text-muted-foreground">Content Creator</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10">
              <Facebook className="size-4" />
            </Button>
            <Button variant="ghost" size="icon" className="rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10">
              <Twitter className="size-4" />
            </Button>
            <Button variant="ghost" size="icon" className="rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10">
              <Linkedin className="size-4" />
            </Button>
            <Button variant="outline" size="sm" className="ml-2 gap-2 rounded-full">
              <Share2 className="size-3.5" />
              Share
            </Button>
          </div>
        </div>
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-12 rounded-2xl overflow-hidden shadow-xl ring-1 ring-border/50">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto object-cover aspect-video"
          />
        </div>
      )}

      {}
      <div 
        className="prose prose-lg dark:prose-invert max-w-none prose-headings:font-bold prose-a:text-primary prose-img:rounded-xl prose-img:shadow-md mb-16"
        dangerouslySetInnerHTML={{ __html: post.content || "" }}
      />

      {}
      <div className="bg-muted/30 rounded-2xl p-8 border border-border mb-16">
        <div className="flex flex-col md:flex-row gap-6 items-center md:items-start text-center md:text-left">
          <Avatar className="size-20 border-4 border-background shadow-md">
            <AvatarImage src={post.author?.avatar?.["96"] || ""} alt={post.author?.name} />
            <AvatarFallback><User className="size-8" /></AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-foreground mb-2">About {post.author?.name}</h3>
            <p className="text-muted-foreground mb-4">
              Expert in digital transformation and cloud technologies. Passionate about helping businesses leverage AI for sustainable growth.
            </p>
            <Link to={post.author?.link || "#"}>
                <Button variant="link" className="p-0 h-auto font-semibold">
                View all posts by {post.author?.name} <ArrowRight className="ml-1 size-3.5" />
                </Button>
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-80 bg-muted rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  if (error || !posts || posts.length === 0) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {posts.map((post, i) => (
        <div key={post.id} data-index={i} className="group h-full">
          <Card className="h-full border-border/50 bg-card/50 hover:bg-card hover:border-primary/20 transition-all duration-300 hover:shadow-lg overflow-hidden flex flex-col">
            {post.featuredImage && (
              <div className="aspect-[16/10] overflow-hidden bg-muted relative">
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            )}
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2 text-xs text-primary font-medium mb-2">
                {post.categories?.[0]?.name || "Blog"}
              </div>
              <CardTitle className="text-xl line-clamp-2 group-hover:text-primary transition-colors">
                <Link to={post.link || "#"}>
                  {post.title}
                </Link>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-grow pb-4">
              <CardDescription className="line-clamp-3 text-sm">
                <div dangerouslySetInnerHTML={{ __html: post.excerpt || "" }} />
              </CardDescription>
            </CardContent>
            <CardFooter className="pt-0 mt-auto border-t border-border/30 p-6">
              <div className="flex items-center justify-between w-full text-sm text-muted-foreground">
                <span className="flex items-center gap-2">
                    <Avatar className="size-6">
                        <AvatarImage src={post.author?.avatar?.["24"]} />
                        <AvatarFallback className="text-[10px]">AU</AvatarFallback>
                    </Avatar>
                    {post.author?.name}
                </span>
                <span>{post.date ? new Date(post.date).toLocaleDateString() : ""}</span>
              </div>
            </CardFooter>
          </Card>
        </div>
      ))}
    </div>
  );
}

export default function SinglePostTemplateSection() {
  
  const wvcClient = getWvcClient();
  const currentParams = wvcClient.getCurrentWPQueryParams();
  const mainQuery = new WP_Query(currentParams);

  
  const relatedQuery = new WP_Query({
    post_type: "post",
    posts_per_page: 3,
    ignore_sticky_posts: true,
  });

  return (
    <section id="single-post-template-section" className="bg-background w-full">
      {}
      <div className="container mx-auto px-4 pt-24 pb-12">
        <PostsProvider wp_query={mainQuery} className="w-full">
          <SinglePostConsumer />
        </PostsProvider>
      </div>

      {}
      <div className="w-full bg-muted/20 border-t border-border py-20">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-end mb-10 gap-4">
            <div>
              <h2 className="text-3xl font-bold tracking-tight text-foreground mb-3">Recommended for you</h2>
              <p className="text-muted-foreground max-w-xl">
                Explore more insights and updates from our team.
              </p>
            </div>
            <Link to="/InsightsPage">
              <Button variant="outline" className="hidden md:flex">View all posts</Button>
            </Link>
          </div>

          <PostsProvider wp_query={relatedQuery} className="w-full">
            <RelatedPostsConsumer />
          </PostsProvider>

          <div className="mt-10 md:hidden text-center">
             <Link to="/InsightsPage">
              <Button variant="outline" className="w-full">View all posts</Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}